/**
 * RESPONSIBILITY: one link in a footer column.
 *
 * `column` groups them, so the number of columns and what goes in each is content rather
 * than layout — the blueprint's Site map / Ministries / Resources are three values of it.
 */
import { Row, Text, Link, Choice } from '@framework/core/decorators';
import { LINK_HINT } from '../../hints';

/**
 * The marks a social row may carry. Permanent values: a stored row names one of these,
 * so renaming one silently blanks an icon.
 */
export const SOCIAL_ICON = {
  None: 'none',
  YouTube: 'youtube',
  Facebook: 'facebook',
  Instagram: 'instagram',
} as const;

export const SOCIAL_ICONS = Object.values(SOCIAL_ICON);

@Row()
export class FooterLink {
  @Text({
    label: 'Column', maxLength: 120,
    description: 'Rows sharing this heading are listed under it. e.g. Site map.',
  })
  column!: string;

  @Text({ label: 'Title', maxLength: 120, description: 'The words shown.' })
  label!: string;

  @Link({ label: 'Link', description: LINK_HINT })
  href!: string;

  /**
   * Only the social row draws one; a link in a column ignores it.
   *
   * CHOSEN, NOT INFERRED. The mark used to be read out of `column` — a field that means
   * "which heading are these links under", which is not a thing a social row has. One
   * field meaning two unrelated things is a field an editor cannot be told the truth
   * about, and the edit menu was asking them for a Column on a row that sits in no
   * column. Guessing it from the label instead fails the first time somebody writes
   * "Our FB page", and fails by drawing nothing.
   */
  @Choice({
    label: 'Mark', options: SOCIAL_ICONS, default: SOCIAL_ICON.None,
    description: 'For a social link: which logo to draw. Ignored in a link column.',
  })
  icon!: string;
}
